import { FlatList, View } from "react-native";
import PlaylistSlider from "../PlaylistSlider";
import PlaylistTitle from "../../components/PlaylistTitle";
import { useNavigation } from "@react-navigation/native";
import { routeAggregatePage, routePage, routeTitleDetails, routeVamAggregatePage, TitleDetailsScreenNavigationProp } from "../../Routes";
import useGenericContentStore from "../../store/genericContent.store";
import Banner from "../Banner";
import { useEffect, useState } from "react";
import HeroBanner from "../../screens/Home/HeroBanner";
import FooterBanner from "../../screens/Home/FooterBanner";
import { CategoryTemplateTypes, CategoryTextTranslation, Orientation } from "../../Types";
import { getCustomerPlaylist, getPlaylist, getPlaylistWithPage, getVamPlaylist } from "../../services/playlistService";
import { getFeaturedLists } from "../../services/apiMoviesContentService";
import { getItem } from "../../utils/Utils";
import CustomizeLoader from "../Loaders/CustomizeLoader";
import CardGridPlaylist from "../CardGridPlaylist";
import { AppLogger } from "../../utils/AppLogger";
import AggregatePageLoader from "../Loaders/AggregatePageLoader";
import { StyledBannerContainer, StyledLibraryFooter } from "./StylesLayout";
import { CategoryOthers } from "../../enums/CategoryOthers";

interface Props {
	location?: string;
	hideTitle?: boolean;
	isSeeMore?: boolean;
	categories?: any;
	transactionalFilters?: string;
	headerBanner?: any;
	footerBanner?: any;
	setIsMyLibraryEmpty?: any;
	isStudioAccess?: boolean;
	isAssignToScreen?: boolean;
	setIsShowLoader: any;
	setStartIndex: any;
	startIndex: number;
	isPreview?: boolean;
	setIsAggregate?: any;
	isHome?: boolean;
	setIsContinueWatching?: any;
	isLibrary?: boolean;
}

const imageLimit = 15;
const appendCount = 1;
const packshots = Array.from({ length: 15 }, (_, i) => i + 1);
const createItem = (id: number, isLandscape: boolean) => ({
	id,
	isLandscape,
	packshot: packshots,
});
const defaultItem = [createItem(2, false)];
const defaultItemLandscape = [createItem(1, true)];
const defaultCategoryType = {
	isHeroBanner: false,
	isBanner: false,
	isLandscape: false,
	isPortrait: false,
	isVAM: false,
};

let controller = new AbortController();
const pageSize = 24;

const Playlist = (props: Props) => {
	const navigationTitleScreen = useNavigation<TitleDetailsScreenNavigationProp>();
	const navigation = useNavigation<any>();

	const { isNarrow, setAggregateTitle } = useGenericContentStore((state: any) => ({
		isNarrow: state.isNarrow,
		setAggregateTitle: state.setAggregateTitle,
	}));

	// Playlist & grid states
	const [playlists, setPlaylists] = useState<any>([]);
	const [gridPlaylist, setGridPlaylist] = useState<any>([]);
	const [isGrid, setIsGrid] = useState(false);
	const [gridSize] = useState(6);
	const [searchLoader, setSearchLoader] = useState(false);
	const [isShowAppendLoader, setIsShowAppendLoader] = useState(false);

	// Pagination & category states
	const [currentPage, setCurrentPage] = useState(1);
	const [next, setNext] = useState(pageSize);
	const [currentCategory, setCurrentCategory] = useState<any>();
	const [categoryType, setCategoryType] = useState<any>(defaultCategoryType);
	const [categoryTranslation, setCategoryTranslation] = useState(false);

	// UI states
	const [isShowHeader, setIsShowHeader] = useState(false);
	const [isShowFooter] = useState(false);

	// Props-based state
	let startIndex = props.startIndex;
	let setStartIndex = props.setStartIndex;

	/**
	 * Handles navigation for playlist items or aggregate pages.
	 * - If the item is a single banner, navigates directly to its detail page.
	 * - If the number of items is below the limit, does nothing.
	 * - Otherwise, sets the aggregate title and navigates to the appropriate aggregate page (VAM or standard).
	 *
	 * @param id - The ID of the playlist or custom list.
	 * @param item - The playlist item object containing metadata and products.
	 * @param length - Number of products in the item.
	 * @param title - The title to set for the aggregate page.
	 * @param isVAM - Boolean indicating whether this is a VAM type playlist.
	 */
	const linkId = (id: any, item: any, length: number, title: string, isVAM: any) => {
		const limit = props?.isStudioAccess ? 7 : imageLimit;

		const categoryOthersId = item.categoryOthers.find((c: any) => c.name === CategoryOthers.navigationId);
		const categoryOthersType = item.categoryOthers.find((c: any) => c.name === CategoryOthers.navigationType);

		if (categoryOthersId && categoryOthersType?.value === CategoryOthers.page) {
			navigation.navigate(routePage, { uniqueId: categoryOthersId.value });
			return;
		}

		if (item.isBanner && item.products.length === 1) {
			const [product] = item.products;
			navigation.navigate(routeTitleDetails, { parentProductId: product.parentProductId });
			return;
		}

		if (length < limit) {
			return;
		}
		setAggregateTitle(title);
		navigation.navigate(isVAM ? routeVamAggregatePage : routeAggregatePage, isVAM ? { customListId: id } : { location: props.location, id });
	};

	/**
	 * Checks if a category should be displayed in landscape layout.
	 * - Returns true for HKA or VAM category types.
	 *
	 * @param category - The category object containing `categoryTemplateType`.
	 * @returns boolean - True if the category uses landscape layout, false otherwise.
	 */
	const isCategoryLandscape = (category: any) =>
		category?.categoryTemplateType === CategoryTemplateTypes.HKA || category?.categoryTemplateType === CategoryTemplateTypes.VAM;

	/**
	 * Checks if a category should be displayed in portrait layout.
	 * - Returns true for standard playlists, continue-watching, or download types.
	 *
	 * @param category - The category object containing `categoryTemplateType`.
	 * @returns boolean - True if the category uses portrait layout, false otherwise.
	 */
	const isCategoryPortrait = (category: any) =>
		category?.categoryTemplateType === CategoryTemplateTypes.STD ||
		category?.categoryTemplateType === CategoryTemplateTypes.ContinueWatching ||
		category?.categoryTemplateType === CategoryTemplateTypes.Download;

	/**
	 * If isMyLibrary is true and props has setIsMyLibrary function
	 * setIsMyLibrary will set true if products.length is zero.
	 * @param products - products from formatPlaylist
	 * @returns
	 */
	const validateMyLibraryIsEmpty = (products: any) =>
		props?.isLibrary && props?.setIsMyLibraryEmpty ? props.setIsMyLibraryEmpty(products?.length === 0) : false;

	/**
	 * If isContinueWatching is true and props has setIsContinueWatching function
	 * setIsContinueWatching will set true if there's product available.
	 * @param isContinueWatching - if category from formatPlaylist is from continue watching carousel
	 * @param products - products from formatPlaylist
	 * @returns
	 */
	const validateIsContinueWatching = (isContinueWatching: boolean, products: any) =>
		isContinueWatching && props?.setIsContinueWatching ? props.setIsContinueWatching(products?.length > 0) : false;

	/**
	 * Determines the rendering type of a category.
	 * Checks if the category should be displayed as HeroBanner, Banner, Landscape (HKA/VAM), Portrait (STD/ContinueWatching/Download), Grid, VAM, or ContinueWatching.
	 *
	 * @param category - The category object containing `categoryTemplateType`.
	 * @returns An object with boolean flags indicating the category type:
	 *  - isHeroBanner: true if category is a Hero Banner
	 *  - isBanner: true if category is a Banner
	 *  - isLandscape: true if category is landscape (HKA or VAM)
	 *  - isPortrait: true if category is portrait (STD, ContinueWatching, Download)
	 *  - isVAM: true if category is VAM
	 *  - isGrid: true if category is Grid type
	 *  - isContinueWatching: true if category is ContinueWatching type
	 */
	const getCategoryType = (category: any) => {
		const isHeroBanner = category?.categoryTemplateType === CategoryTemplateTypes.FeatureTemplate;
		const isBanner = category?.categoryTemplateType === CategoryTemplateTypes.Banner;
		const isLandscape = isCategoryLandscape(category);
		const isPortrait = isCategoryPortrait(category);
		const isVAM = category?.categoryTemplateType === CategoryTemplateTypes.VAM;
		const isGrid = category?.categoryTemplateType === CategoryTemplateTypes.Grid;
		const isContinueWatching = category?.categoryTemplateType === CategoryTemplateTypes.ContinueWatching;

		return {
			isHeroBanner,
			isBanner,
			isLandscape,
			isPortrait,
			isVAM,
			isGrid,
			isContinueWatching,
		};
	};

	/**
	 * Formatting playlist object to determine if categories will render as
	 * (HeroBanner, Banner, HKA, STD or VAM)
	 * @param category - category from props.categories
	 * @param playlist - playlist per category from fetchPlaylist
	 * @returns
	 */
	const formatPlaylist = (category: any, playlist: any) => {
		const { isHeroBanner, isBanner, isLandscape, isPortrait, isVAM, isGrid, isContinueWatching } = getCategoryType(category);

		let translatedTitle = category.categoryTextTranslations.find((translation: CategoryTextTranslation) => {
			if (translation.name.toLowerCase() == "title") return true;
		});

		let title = translatedTitle?.text ?? "";
		let products = isHeroBanner ? playlist.featureList : playlist.products || [];

		if (title) setCategoryTranslation(true);

		validateMyLibraryIsEmpty(products);
		validateIsContinueWatching(isContinueWatching, products);

		if (isContinueWatching) products = products.slice(0, 15);

		return {
			isLandscape: isLandscape,
			isPortrait: isPortrait,
			isProgressBar: false,
			playListCarousel: true,
			playlistId: category.playlistId || category.consumerPlaylistId,
			customListId: category.customListId,
			categoryName: category.name,
			title: title,
			rawTitle: category.name,
			isVam: isVAM,
			isBanner: isBanner,
			categoryImages: category?.categoryImages,
			uri: null,
			products: isVAM ? products.slice(0, 20) : products,
			id: category.id,
			isHeroBanner,
			isGrid,
			isContinueWatching,
			categoryOthers: category.categoryOthers,
		};
	};

	/**
	 * Fetches and formats a playlist based on the category type.
	 *
	 * @param category - The category object containing playlist IDs and type.
	 * @param signal - Abort signal for cancellable requests.
	 * @returns Formatted playlist data.
	 */
	const fetchPlaylist = async (category: any, signal: any) => {
		setCurrentCategory(category);
		let playlist;

		if (category.playlistId) {
			const isGrid = category.categoryTemplateType === CategoryTemplateTypes.Grid;
			const isBanner = category.categoryTemplateType === CategoryTemplateTypes.Banner;
			playlist =
				isGrid || isBanner
					? await getPlaylistWithPage(category.playlistId, false, pageSize, currentPage, props?.isPreview, Orientation.all)
					: await getPlaylist(category.playlistId, false, signal, props?.isPreview, Orientation.all);
		} else if (category.customListId) {
			playlist = await getVamPlaylist(category.customListId, 15, 1, signal);
		} else if (category.consumerPlaylistId) {
			if (props?.isPreview) return [];
			playlist = await getCustomerPlaylist(category.consumerPlaylistId, props.transactionalFilters, 15, 1, signal, Orientation.all);
		} else {
			playlist = await getFeaturedLists(category.id, props.transactionalFilters, signal, props?.isPreview);
		}

		return formatPlaylist(category, playlist);
	};

	/**
	 * Handles the press action for a playlist item.
	 *
	 * If the item contains navigation metadata (`navigation-id` and `navigation-type`)
	 * and its type indicates a page navigation, it navigates to the specified page.
	 * Otherwise, it navigates to the title details screen, enabling preview mode if applicable.
	 *
	 * @param parentProductId - The ID of the parent product to navigate to.
	 * @param item - The playlist item containing category metadata for navigation.
	 */
	const onPress = (parentProductId: any, item?: any) => {
		const defaultRoute = {
			parentProductId,
			...(props?.isPreview && { preview: true }),
		};

		const categoryOthersId = item.categoryOthers.find((c: any) => c.name === CategoryOthers.navigationId);
		const categoryOthersType = item.categoryOthers.find((c: any) => c.name === CategoryOthers.navigationType);

		if (categoryOthersId && categoryOthersType?.value === CategoryOthers.page) {
			navigation.navigate(routePage, { uniqueId: categoryOthersId.value });
			return;
		}

		props?.isPreview
			? navigation.navigate(routeTitleDetails, defaultRoute)
			: navigationTitleScreen.navigate(routeTitleDetails, { parentProductId });
	};

	/**
	 * Render HeroBanner component
	 * @param products - playlist/item from renderItem
	 * @returns
	 */
	const renderHeroBanner = ({ products }: any) => {
		return (
			//@ts-ignore
			<StyledBannerContainer isPreview={props?.isPreview}>
				<HeroBanner heroBanner={products} isPreview={props?.isPreview} />
			</StyledBannerContainer>
		);
	};

	/**
	 * Renders the playlist content for a category item.
	 * - Displays either a PlaylistSlider, CardGridPlaylist, or a loader based on category type.
	 *
	 * @param ID - The ID of the category or custom list.
	 * @param item - The category item containing type flags and products.
	 * @returns JSX.Element - The rendered playlist components.
	 */
	const renderPlaylist = (ID: any, item: any) => {
		// Skip rendering for hero banners or banners
		if (item?.isHeroBanner || item.isBanner) return null;

		const isVamOrStudio = props?.isStudioAccess || item?.isVam;

		return (
			<>
				{!item.isGrid && (
					<PlaylistSlider
						id={ID}
						title={item.title}
						products={isVamOrStudio ? item.vamProducts : item.products}
						isLandscape={item.isLandscape}
						customListId={isVamOrStudio ? ID : null}
						isStudioAccess={isVamOrStudio}
						isAssignToScreen={props?.isAssignToScreen}
						isSeeMore={props?.isPreview || item?.isContinueWatching ? false : props.isSeeMore}
						seeMoreLength={imageLimit}
						isProgressBar={item.isProgressBar}
						location={props.location}
						isPreview={props?.isPreview}
						isHome={props?.isHome}
					/>
				)}

				{item.isGrid && (
					<>
						<CardGridPlaylist
							isHome={props.isHome}
							data={gridPlaylist}
							parentProductId={ID}
							isAggregate={true}
							isPreview={props?.isPreview}
						/>
						{searchLoader && (
							<View style={{ marginTop: 20 }}>
								<AggregatePageLoader isVam={false} slice={gridSize} />
							</View>
						)}
					</>
				)}
			</>
		);
	};

	/**
	 * Determines the left margin value based on screen, preview, and studio access state.
	 *
	 * @returns {number} - 30 if narrow + studio, 64 if narrow + preview, 110 if narrow, 97 if preview, otherwise 151.
	 */
	const getMarginLeft = (isNarrow: boolean, isPreview?: boolean, isStudioAccess?: boolean): number => {
		if (isNarrow) {
			if (isStudioAccess) return 30;
			if (isPreview) return 64;
			return 110;
		}

		if (isPreview) return 97;
		return 151;
	};

	/**
	 * Renders a single item in the playlist.
	 * Handles multiple types including HeroBanner, Grid, Banner, and standard playlists.
	 *
	 * Behavior:
	 * - Returns null if the item has no products and the user has studio access.
	 * - Sets grid state if the item is a grid and initializes `gridPlaylist`.
	 * - Renders:
	 *   - HeroBanner via `renderHeroBanner`
	 *   - PlaylistTitle for non-hero, non-grid items
	 *   - Playlist content via `renderPlaylist`
	 *   - Banner component for banner items
	 *
	 * @param item - The playlist item object with metadata, products, and flags.
	 * @returns JSX.Element | null - The rendered playlist item component(s) or null.
	 */
	const renderItem = ({ item }: any) => {
		if (!item?.products?.length) return null;
		const isClickable = item.products.length <= 14;
		const ID = props?.isStudioAccess ? item.customListId : item.id;

		if (item.isGrid) {
			setIsGrid(item.isGrid);
			if (!gridPlaylist.length) setGridPlaylist(item.products);
		}

		const marginLeft = getMarginLeft(isNarrow, props?.isPreview, props?.isStudioAccess);

		return (
			<>
				{item?.isHeroBanner && renderHeroBanner(item)}

				{!item?.isHeroBanner && !props?.hideTitle && !item.isGrid ? (
					<View style={{ marginLeft }}>
						<PlaylistTitle
							isNarrow={isNarrow}
							title={item.title}
							isContinueWatching={item.isContinueWatching}
							onPress={() =>
								isClickable ? {} : !props?.isPreview && linkId(ID, item, item.products.length, item.title, props?.isStudioAccess)
							}
							noSpacing={props?.isStudioAccess}
							hasCategoryTranslation={categoryTranslation}
							removeCursor={isClickable}
						/>
					</View>
				) : (
					<></>
				)}

				{renderPlaylist(ID, item)}

				{!item?.isHeroBanner && item.isBanner ? (
					<View>
						<Banner item={item} onPress={(product) => onPress(product, item)} isPreview={props.isPreview} />
					</View>
				) : (
					<></>
				)}
			</>
		);
	};

	/**
	 * Handles lazy loading of playlists in grid view when the user reaches the end.
	 * - Increments the current page number and fetches the next page of playlists.
	 * - Updates the grid playlist state and the `next` counter for pagination.
	 * - Stops further loading if there are no more playlists.
	 * - Logs errors if fetching fails and ensures the loader is hidden after completion.
	 */
	const onEndReachedGrid = async () => {
		if (next > gridPlaylist?.length) {
			setSearchLoader(false);
			return;
		}

		setCurrentPage((prev) => prev + 1);

		try {
			const page = await getPlaylistWithPage(currentCategory.playlistId, false, pageSize, currentPage + 1);
			const nextPlaylist = formatPlaylist(currentCategory, page);
			const [nextPageResults] = getItem([nextPlaylist]);

			if (nextPageResults) {
				setGridPlaylist((prev: any) => prev.concat(nextPageResults.products));
				setNext((prev) => prev + pageSize);
			}
		} catch (err) {
			AppLogger.log(err);
		} finally {
			setSearchLoader(false);
		}
	};

	/**
	 * Handles lazy loading of additional playlists when the user reaches the end of the list.
	 * - Supports both grid and standard playlist views.
	 * - Prevents multiple concurrent fetches using `isShowAppendLoader`.
	 * - Updates the playlist state, category type, and start index.
	 * - Uses `AbortController` to allow request cancellation if needed.
	 */
	const onEndReached = () => {
		controller = new AbortController();

		if (isGrid) {
			setSearchLoader(true);
			onEndReachedGrid();
			return;
		}

		if (isShowAppendLoader) return;

		const categories = props.categories.slice(startIndex, startIndex + appendCount);
		const firstCategory = categories[0];

		if (firstCategory) setCategoryType(getCategoryType(firstCategory));
		setIsShowAppendLoader(true);

		Promise.all(categories.map((category: any) => fetchPlaylist(category, controller.signal)))
			.then((products) => {
				setPlaylists((prev: any) => prev.concat(getItem(products)));
				setStartIndex(startIndex + appendCount);
			})
			.finally(() => setIsShowAppendLoader(false));
	};

	/**
	 * Renders a customized loader component for playlists, banners, or hero banners.
	 * The loader type and data are determined based on the current `categoryType`.
	 *
	 * @returns JSX.Element - The loader component.
	 */
	const renderAppendLoader = () => (
		<CustomizeLoader
			show={isShowAppendLoader}
			isPlaylist={categoryType?.isLandscape || categoryType?.isPortrait || categoryType?.isVAM}
			isBanner={categoryType?.isBanner}
			isHeroBanner={categoryType?.isHeroBanner}
			isVAM={categoryType.isVAM}
			isStudioAccess={props.isStudioAccess}
			data={categoryType?.isPortrait ? defaultItem : defaultItemLandscape}
			isPreview={props.isPreview}
			isHome={props.isHome}
		/>
	);

	/**
	 * if isShowFooter is true it will return the FooterBanner
	 * if not it will return the shimmer loader
	 * @returns footerBanner from props or loader
	 */
	const renderFooter = () => (isShowFooter && props?.footerBanner ? <FooterBanner banner={props.footerBanner} /> : renderAppendLoader());

	/**
	 * Render Library footer
	 * @returns
	 */
	const renderLibraryFooter = () => <StyledLibraryFooter />;

	/**
	 * Render header Banner
	 * @returns headerBanner from props
	 */
	const renderHeader = () => (isShowHeader && props?.headerBanner ? props.headerBanner : <></>);

	/**
	 * Returns the style object for the Playlist container.
	 * Adjusts padding based on screen width and studio access.
	 *
	 * @returns {{ paddingTop: string, height: string, paddingBottom?: string }}
	 */
	const getPlaylistStyle = () => ({
		paddingTop: isNarrow ? "70px" : "7rem",
		height: "100vh",
		...(props?.isStudioAccess && { paddingBottom: "8rem" }),
	});

	/**
	 * Fetches playlists for the given categories when `props.categories` changes.
	 * - Aborts any ongoing fetch if the component unmounts or categories change.
	 * - Updates local state `playlists` and header visibility.
	 * - Controls loader and aggregate state based on props.
	 * - Cleans up state on unmount or dependency change.
	 */
	useEffect(() => {
		if (!props.categories.length) return;

		const controller = new AbortController();
		const categories = props.categories.slice(0, startIndex);

		Promise.all(categories.map((category: any) => fetchPlaylist(category, controller.signal)))
			.then((products) => {
				setPlaylists(getItem(products));
				props?.setIsShowLoader(false);
				if (props?.isPreview) props?.setIsAggregate(true);
				setIsShowHeader(props?.headerBanner);
			})
			.catch((err) => {
				AppLogger.log(err); // optional: handle errors
			});

		return () => {
			setPlaylists([]);
			props?.setIsShowLoader(true);
			if (props?.isPreview) props?.setIsAggregate(false);
		};
	}, [props.categories]);

	return (
		<FlatList
			data={playlists}
			renderItem={renderItem}
			style={getPlaylistStyle()}
			keyExtractor={(item) => item.id}
			onEndReached={onEndReached}
			onEndReachedThreshold={0.8}
			ListHeaderComponent={renderHeader}
			ListFooterComponent={props?.isLibrary ? renderLibraryFooter : renderFooter}
			ListHeaderComponentStyle={{}}
			decelerationRate={"normal"}
			contentContainerStyle={{
				paddingBottom: props?.isPreview ? 120 : 40,
				top: isNarrow && props.isStudioAccess ? 40 : 0,
			}}
		/>
	);
};

export default Playlist;
